/**
 * XPrime Theme JavaScript
 * Modern E-Commerce Functionality
 */

(function() {
  'use strict';

  // Theme Object
  const XPrime = {
    init: function() {
      this.lazyLoadImages();
      this.initMobileMenu();
      this.initSearch();
      this.initCart();
      this.initProductQuickView();
      this.initNotifications();
    },

    // Lazy Load Images
    lazyLoadImages: function() {
      if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              img.src = img.dataset.src;
              img.classList.add('loaded');
              observer.unobserve(img);
            }
          });
        });

        document.querySelectorAll('img[loading="lazy"]').forEach(img => {
          imageObserver.observe(img);
        });
      }
    },

    // Mobile Menu
    initMobileMenu: function() {
      const menuToggle = document.querySelector('[data-mobile-menu-toggle]');
      const mobileMenu = document.querySelector('[data-mobile-menu]');
      
      if (menuToggle && mobileMenu) {
        menuToggle.addEventListener('click', () => {
          mobileMenu.classList.toggle('is-active');
          document.body.classList.toggle('menu-open');
        });
      }
    },

    // Search Functionality
    initSearch: function() {
      const searchToggle = document.querySelector('[data-search-toggle]');
      const searchModal = document.querySelector('[data-search-modal]');
      const searchInput = document.querySelector('[data-search-input]');
      
      if (searchToggle && searchModal) {
        searchToggle.addEventListener('click', () => {
          searchModal.classList.add('is-active');
          if (searchInput) searchInput.focus();
        });

        // Close on backdrop click
        const backdrop = searchModal.querySelector('.modal__backdrop');
        if (backdrop) {
          backdrop.addEventListener('click', () => {
            searchModal.classList.remove('is-active');
          });
        }

        // Predictive Search
        if (searchInput) {
          let searchTimeout;
          searchInput.addEventListener('input', (e) => {
            clearTimeout(searchTimeout);
            searchTimeout = setTimeout(() => {
              this.performSearch(e.target.value);
            }, 300);
          });
        }
      }
    },

    // Perform Search
    performSearch: function(query) {
      if (query.length < 2) return;

      fetch(`${window.theme.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`)
        .then(response => response.json())
        .then(data => {
          this.displaySearchResults(data);
        })
        .catch(error => {
          console.error('Search error:', error);
        });
    },

    // Display Search Results
    displaySearchResults: function(data) {
      const resultsContainer = document.querySelector('[data-search-results]');
      if (!resultsContainer) return;

      if (data.resources.results.products.length === 0) {
        resultsContainer.innerHTML = '<p class="text-center">Ürün bulunamadı</p>';
        return;
      }

      const html = data.resources.results.products.map(product => `
        <a href="${product.url}" class="search-result">
          <img src="${product.featured_image.url}" alt="${product.title}" loading="lazy">
          <div>
            <h4>${product.title}</h4>
            <p>${product.price}</p>
          </div>
        </a>
      `).join('');

      resultsContainer.innerHTML = html;
    },

    // Cart Functionality
    initCart: function() {
      const cartToggle = document.querySelector('[data-cart-toggle]');
      const cartDrawer = document.querySelector('[data-cart-drawer]');
      
      if (cartToggle && cartDrawer) {
        cartToggle.addEventListener('click', () => {
          this.openCart();
        });
      }

      // Add to Cart Forms
      document.querySelectorAll('[data-add-to-cart-form]').forEach(form => {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.addToCart(form);
        });
      });

      // Quick Add to Cart Buttons
      document.querySelectorAll('.product-card__quick-add').forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const productId = button.dataset.productId;
          if (productId) {
            this.quickAddToCart(productId, button);
          }
        });
      });
    },

    // Quick Add to Cart
    quickAddToCart: function(productId, button) {
      button.disabled = true;
      const originalText = button.innerHTML;
      button.innerHTML = '<i data-lucide="loader" width="18" height="18"></i> Ekleniyor...';
      
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: productId,
            quantity: 1
          }]
        })
      })
      .then(response => response.json())
      .then(data => {
        this.updateCartCount();
        if (typeof openCartDrawer === 'function') {
          openCartDrawer();
        }
        this.showNotification('Ürün sepete eklendi!', 'success');
      })
      .catch(error => {
        console.error('Add to cart error:', error);
        this.showNotification('Bir hata oluştu', 'error');
      })
      .finally(() => {
        button.disabled = false;
        button.innerHTML = originalText;
        if (typeof lucide !== 'undefined') {
          lucide.createIcons();
        }
      });
    },

    // Add to Cart
    addToCart: function(form) {
      const formData = new FormData(form);
      const button = form.querySelector('[type="submit"]');
      
      button.classList.add('loading');
      button.disabled = true;

      fetch(window.theme.routes.cart_add_url, {
        method: 'POST',
        body: formData
      })
      .then(response => response.json())
      .then(data => {
        this.updateCartCount();
        this.openCart();
        this.showNotification('Ürün sepete eklendi', 'success');
      })
      .catch(error => {
        console.error('Add to cart error:', error);
        this.showNotification('Bir hata oluştu', 'error');
      })
      .finally(() => {
        button.classList.remove('loading');
        button.disabled = false;
      });
    },

    // Open Cart
    openCart: function() {
      const cartDrawer = document.querySelector('[data-cart-drawer]');
      if (cartDrawer) {
        cartDrawer.classList.add('is-active');
        this.fetchCart();
      }
    },

    // Fetch Cart
    fetchCart: function() {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          this.renderCart(cart);
        })
        .catch(error => {
          console.error('Fetch cart error:', error);
        });
    },

    // Render Cart
    renderCart: function(cart) {
      const cartContainer = document.querySelector('[data-cart-items]');
      if (!cartContainer) return;

      if (cart.items.length === 0) {
        cartContainer.innerHTML = '<p class="text-center">Sepetiniz boş</p>';
        return;
      }

      const html = cart.items.map(item => `
        <div class="cart-item" data-line-item="${item.key}">
          <img src="${item.featured_image.url}" alt="${item.title}">
          <div class="cart-item__details">
            <h4>${item.product_title}</h4>
            <p>${item.variant_title}</p>
            <div class="cart-item__quantity">
              <button data-cart-decrease="${item.key}">-</button>
              <input type="number" value="${item.quantity}" min="1" data-cart-quantity="${item.key}">
              <button data-cart-increase="${item.key}">+</button>
            </div>
          </div>
          <div class="cart-item__price">
            <p>${this.formatMoney(item.final_line_price)}</p>
            <button data-cart-remove="${item.key}">Sil</button>
          </div>
        </div>
      `).join('');

      cartContainer.innerHTML = html;
      
      // Update total
      const totalElement = document.querySelector('[data-cart-total]');
      if (totalElement) {
        totalElement.textContent = this.formatMoney(cart.total_price);
      }

      // Bind cart actions
      this.bindCartActions();
    },

    // Bind Cart Actions
    bindCartActions: function() {
      // Remove item
      document.querySelectorAll('[data-cart-remove]').forEach(button => {
        button.addEventListener('click', () => {
          this.updateCartItem(button.dataset.cartRemove, 0);
        });
      });

      // Increase quantity
      document.querySelectorAll('[data-cart-increase]').forEach(button => {
        button.addEventListener('click', () => {
          const input = document.querySelector(`[data-cart-quantity="${button.dataset.cartIncrease}"]`);
          if (input) {
            this.updateCartItem(button.dataset.cartIncrease, parseInt(input.value) + 1);
          }
        });
      });

      // Decrease quantity
      document.querySelectorAll('[data-cart-decrease]').forEach(button => {
        button.addEventListener('click', () => {
          const input = document.querySelector(`[data-cart-quantity="${button.dataset.cartDecrease}"]`);
          if (input && parseInt(input.value) > 1) {
            this.updateCartItem(button.dataset.cartDecrease, parseInt(input.value) - 1);
          }
        });
      });
    },

    // Update Cart Item
    updateCartItem: function(key, quantity) {
      fetch(window.theme.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      })
      .then(response => response.json())
      .then(cart => {
        this.renderCart(cart);
        this.updateCartCount();
      })
      .catch(error => {
        console.error('Update cart error:', error);
      });
    },

    // Update Cart Count
    updateCartCount: function() {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const countElements = document.querySelectorAll('[data-cart-count]');
          countElements.forEach(element => {
            element.textContent = cart.item_count;
          });
        });
    },

    // Product Quick View
    initProductQuickView: function() {
      document.querySelectorAll('[data-quick-view]').forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const productHandle = button.dataset.quickView;
          this.openQuickView(productHandle);
        });
      });
    },

    // Open Quick View
    openQuickView: function(handle) {
      fetch(`/products/${handle}?view=quick`)
        .then(response => response.text())
        .then(html => {
          const modal = document.querySelector('[data-quick-view-modal]');
          if (modal) {
            modal.querySelector('.modal__content').innerHTML = html;
            modal.classList.add('is-active');
          }
        })
        .catch(error => {
          console.error('Quick view error:', error);
        });
    },

    // Notifications
    initNotifications: function() {
      // Auto-hide notifications after 5 seconds
      document.querySelectorAll('.notification').forEach(notification => {
        setTimeout(() => {
          notification.style.opacity = '0';
          setTimeout(() => {
            notification.remove();
          }, 300);
        }, 5000);
      });
    },

    // Show Notification
    showNotification: function(message, type = 'success') {
      const notification = document.createElement('div');
      notification.className = `notification notification--${type}`;
      notification.textContent = message;
      document.body.appendChild(notification);

      setTimeout(() => {
        notification.style.opacity = '0';
        setTimeout(() => {
          notification.remove();
        }, 300);
      }, 5000);
    },

    // Format Money
    formatMoney: function(cents) {
      const format = window.theme.moneyFormat || '{{amount}} TL';
      const value = (cents / 100).toFixed(2);
      return format.replace('{{amount}}', value);
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      XPrime.init();
      // Initialize Lucide Icons
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    });
  } else {
    XPrime.init();
    // Initialize Lucide Icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
  }

  // Expose to window
  window.XPrime = XPrime;

})();
